// Mirrors the backend Zod password schemas (backend/src/routes/auth.js signup + /me/password,
// backend/src/routes/cmOnboarding.js). Keep the two in sync: a rule change here needs the same
// change there. Regexes are ASCII-only on purpose, like the backend ("É" is not an uppercase match),
// and length is String.length (UTF-16 code units), same as Zod's .min().

export interface PasswordRule {
    id: 'length' | 'upper' | 'lower' | 'number'
    label: string
    test: (password: string) => boolean
}

export const PASSWORD_MIN_LENGTH = 8

export const PASSWORD_RULES: readonly PasswordRule[] = [
    { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: p => p.length >= PASSWORD_MIN_LENGTH },
    { id: 'upper', label: 'An uppercase letter (A–Z)', test: p => /[A-Z]/.test(p) },
    { id: 'lower', label: 'A lowercase letter (a–z)', test: p => /[a-z]/.test(p) },
    { id: 'number', label: 'A number (0–9)', test: p => /[0-9]/.test(p) },
]

export function meetsPasswordPolicy(password: string): boolean {
    return PASSWORD_RULES.every(rule => rule.test(password))
}

export type PasswordStrength = 'Weak' | 'Fair' | 'Strong'

// Rule-based, not entropy: anything failing the policy is Weak, passing it is Fair,
// passing it with 12+ characters and a symbol is Strong.
export function passwordStrength(password: string): PasswordStrength {
    if (!meetsPasswordPolicy(password)) return 'Weak'
    return password.length >= 12 && /[^A-Za-z0-9]/.test(password) ? 'Strong' : 'Fair'
}
